import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCatalog } from "../api/client.js";
import { messages, type SortOrder } from "../i18n/messages.js";
import type { ProductCardDTO } from "../api/types.js";

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

export interface CatalogState {
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  query: string;
  category: string | null;
  collection: string | null;
  sortOrder: SortOrder;
  results: ProductCardDTO[];
  nextCursor: string | null;
  setQuery: (value: string) => void;
  setCategory: (value: string | null) => void;
  setCollection: (value: string | null) => void;
  setSortOrder: (value: SortOrder) => void;
  loadMore: () => void;
  retry: () => void;
  resetFilters: () => void;
}

export interface CatalogInitialFilters {
  category?: string | null;
  collection?: string | null;
}

/**
 * FUNCTIONALITY-093 search contract (client passes through to Worker token_v1):
 * - query empty + category → category products
 * - query non-empty + all categories → global search
 * - query non-empty + category → intersection
 */
export function useCatalog(initialFilters: CatalogInitialFilters = {}): CatalogState {
  const [query, setQueryState] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [category, setCategory] = useState<string | null>(initialFilters.category ?? null);
  const [collection, setCollection] = useState<string | null>(initialFilters.collection ?? null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("default");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ProductCardDTO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setCategory(initialFilters.category ?? null);
    setCollection(initialFilters.collection ?? null);
  }, [initialFilters.category, initialFilters.collection]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const setQuery = useCallback((value: string) => {
    setQueryState(value);
  }, []);

  const retry = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  const resetFilters = useCallback(() => {
    setQueryState("");
    setCategory(null);
    setCollection(null);
    setSortOrder("default");
    setReloadToken((value) => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++requestIdRef.current;

    async function loadInitial() {
      setLoading(true);
      setError(null);
      setResults([]);
      setNextCursor(null);

      try {
        const params: {
          limit: number;
          q?: string;
          category?: string;
          collection?: string;
        } = { limit: PAGE_SIZE };
        if (debouncedQuery) {
          params.q = debouncedQuery;
        }
        if (category) {
          params.category = category;
        }
        if (collection) {
          params.collection = collection;
        }
        const data = await fetchCatalog(params);
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }
        setResults(data.results);
        setNextCursor(data.next_cursor);
      } catch (err) {
        if (!cancelled && requestId === requestIdRef.current) {
          setError(err instanceof Error ? err.message : messages.catalog.catalogFailed);
        }
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }

    void loadInitial();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, category, collection, reloadToken]);

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) {
      return;
    }

    const cursor = nextCursor;
    void (async () => {
      setLoadingMore(true);
      setError(null);
      try {
        const params: {
          limit: number;
          cursor: string;
          q?: string;
          category?: string;
          collection?: string;
        } = {
          limit: PAGE_SIZE,
          cursor,
        };
        if (debouncedQuery) {
          params.q = debouncedQuery;
        }
        if (category) {
          params.category = category;
        }
        if (collection) {
          params.collection = collection;
        }
        const data = await fetchCatalog(params);
        setResults((current) => [...current, ...data.results]);
        setNextCursor(data.next_cursor);
      } catch (err) {
        setError(err instanceof Error ? err.message : messages.catalog.loadMoreFailed);
      } finally {
        setLoadingMore(false);
      }
    })();
  }, [category, collection, debouncedQuery, loadingMore, nextCursor]);

  return {
    loading,
    loadingMore,
    error,
    query,
    category,
    collection,
    sortOrder,
    results,
    nextCursor,
    setQuery,
    setCategory,
    setCollection,
    setSortOrder,
    loadMore,
    retry,
    resetFilters,
  };
}
